// BOORO: what every area shares to look over all offices: the organigram, the
// filter in the address, and a client that talks to the office of an item.
import type { OrgScope } from "@booro/team";
import { EMPTY_SCOPE, orderedGroups } from "@booro/team";
import { useLingui } from "@lingui/react/macro";
import type { OrgSnapshot } from "@rakazo/contracts";
import { Button, NativeSelect, NativeSelectOption } from "@rakazo/ui-web";
import { X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { RpcClient } from "../../lib/rpc";
import { createSpaceRpc, rpc } from "../../lib/rpc";
import { errorText } from "../../lib/user-error";

/** How often the organigram reloads while an area shows it. */
const REFRESH_MS = 15_000;

const officeClients = new Map<string, RpcClient>();

/** The client for the office an item lives in, so changing it never switches the app. */
export function officeRpc(spaceId: string): RpcClient {
  let client = officeClients.get(spaceId);
  if (!client) {
    client = createSpaceRpc(spaceId);
    officeClients.set(spaceId, client);
  }
  return client;
}

/** Opens the chat of an agent, switching to its office when it lies in another one. */
export function openAgentChat(spaceId: string, path: string): void {
  window.dispatchEvent(new CustomEvent("booro:open-chat", { detail: { spaceId, path } }));
}

/** Every office of the person with its groups and agents. */
export function useOrgSnapshot(): {
  snapshot: OrgSnapshot | null;
  error: string;
  reload: () => Promise<void>;
} {
  const [snapshot, setSnapshot] = useState<OrgSnapshot | null>(null);
  const [error, setError] = useState("");
  const reload = useCallback(async () => {
    try {
      setSnapshot(await rpc.booroOrg.snapshot());
      setError("");
    } catch (cause) {
      setError(errorText(cause));
    }
  }, []);
  useEffect(() => {
    void reload();
    const timer = window.setInterval(() => void reload(), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [reload]);
  return { snapshot, error, reload };
}

const SCOPE_PARAMS = { spaceId: "buero", groupId: "gruppe", botId: "agent" } as const;

/** The filter of the areas, kept in the address so it survives a switch of area. */
export function useOrgScope(): [OrgScope, (next: OrgScope) => void] {
  const [params, setParams] = useSearchParams();
  const scope: OrgScope = {
    spaceId: params.get(SCOPE_PARAMS.spaceId),
    groupId: params.get(SCOPE_PARAMS.groupId),
    botId: params.get(SCOPE_PARAMS.botId),
  };
  const setScope = useCallback(
    (next: OrgScope) =>
      setParams(
        (previous) => {
          const out = new URLSearchParams(previous);
          for (const key of Object.keys(SCOPE_PARAMS) as (keyof OrgScope)[]) {
            const value = next[key];
            if (value) out.set(SCOPE_PARAMS[key], value);
            else out.delete(SCOPE_PARAMS[key]);
          }
          return out;
        },
        { replace: true },
      ),
    [setParams],
  );
  return [scope, setScope];
}

/** The filter bar: office, group, agent. Each choice narrows the next. */
export function ScopeFilter({
  snapshot,
  scope,
  onChange,
}: {
  snapshot: OrgSnapshot;
  scope: OrgScope;
  onChange: (next: OrgScope) => void;
}) {
  const { t } = useLingui();
  const groups = orderedGroups(snapshot.groups, scope.spaceId);
  const agents = snapshot.agents.filter(
    (agent) => !scope.spaceId || agent.spaceId === scope.spaceId,
  );
  const officeName = (spaceId: string) =>
    snapshot.offices.find((office) => office.id === spaceId)?.name ?? "";
  const severalOffices = snapshot.offices.length > 1;
  const narrowed = Boolean(scope.spaceId || scope.groupId || scope.botId);

  return (
    <div data-testid="scope-filter" className="flex flex-wrap items-center gap-2">
      {severalOffices ? (
        <NativeSelect
          aria-label={t`Office`}
          value={scope.spaceId ?? ""}
          onChange={(event) => onChange({ ...EMPTY_SCOPE, spaceId: event.target.value || null })}
        >
          <NativeSelectOption value="">{t`All offices`}</NativeSelectOption>
          {snapshot.offices.map((office) => (
            <NativeSelectOption key={office.id} value={office.id}>
              {office.name}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      ) : null}
      {groups.length > 0 ? (
        <NativeSelect
          aria-label={t({ message: "Group", context: "organigram" })}
          value={scope.groupId ?? ""}
          onChange={(event) =>
            onChange({ spaceId: scope.spaceId, groupId: event.target.value || null, botId: null })
          }
        >
          <NativeSelectOption value="">{t`All groups`}</NativeSelectOption>
          {groups.map(({ group, depth }) => (
            <NativeSelectOption key={group.id} value={group.id}>
              {`${" ".repeat(depth)}${group.name}${
                severalOffices && !scope.spaceId && depth === 0
                  ? ` · ${officeName(group.spaceId)}`
                  : ""
              }`}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      ) : null}
      <NativeSelect
        aria-label={t`Agent`}
        value={scope.botId ?? ""}
        onChange={(event) => onChange({ ...scope, botId: event.target.value || null })}
      >
        <NativeSelectOption value="">{t`All agents`}</NativeSelectOption>
        {agents.map((agent) => (
          <NativeSelectOption key={agent.id} value={agent.id}>
            {severalOffices && !scope.spaceId
              ? `${agent.name} · ${officeName(agent.spaceId)}`
              : agent.name}
          </NativeSelectOption>
        ))}
      </NativeSelect>
      {narrowed ? (
        <Button
          size="sm"
          variant="ghost"
          aria-label={t`Show everything`}
          onClick={() => onChange(EMPTY_SCOPE)}
        >
          <X />
          {t`Everything`}
        </Button>
      ) : null}
    </div>
  );
}
